<template>
  <div>
    <ValidationGroup class="structured-container" alwaysSubmit @submit="pay" ref="form">
      <div class="structured-top pb-0">
        <h1 v-mark-word>{{ $t('purchase.title') }}</h1>
        <router-link class="btn btn-default float-end" :to="{ name: `report-${this.$i18n.locale}`, params: { ...product, lang: this.$i18n.locale } }">
          {{ $t('purchase.backToProductPage') }}
        </router-link>
      </div>
      <div class="structured-content no-background pt-3">
        <div class="structured-header negative">
          <slot name="header" />
        </div>
        <div class="structured-body positive">
          <slot />
          <div class="label-group">
            <span class="label-heading">{{ $t('purchase.confirmation') }}</span>
          </div>
          <ConfirmationForm
            scope="purchase"
            type="email"
            name="email"
            email
            required
            v-model="details.confirmationEmail"
          />
          <div class="alert alert-default mt-5" role="alert">
            {{ $t('purchase.contactConfirmationHint') }}
          </div>
        </div>
        <ValidationField class="structured-footer negative" required highlight type="checkbox" :value="readContract">
          <template v-slot:default="{ validityClass }">
            <Checkbox
              class="mb-7"
              label-class="mb-0 mt-0"
              :class="validityClass"
              v-model="readContract"
            >
              <template v-slot:default>
                {{ $t('purchase.read') }}
                <a :href="$t('purchase.contractLink')" target="_blank">
                  {{ $t('purchase.contract') }}
                </a>
                <span class="fw-normal">({{ $t('form.required') }})</span>
              </template>
              <template v-slot:error>
                <span class="invalid-feedback">{{ $t('purchase.validation.agreement') }}</span>
              </template>
            </Checkbox>
            <Messages id="purchase" class="mb-7" v-if="!form.touched || form.submitted && !form.valid" />
            <div class="structured-inner-footer flex-wrap">
              <button class="btn btn-lg btn-success" :disabled="!!purchaseSession">{{ $t('purchase.pay') }}</button>
              <div class="text-center text-md-end">
                <span class="price">{{ $price(calculatedPrice) }}</span>
                <span class="caption d-block">{{ $t('purchase.priceHint', { vatPercent }) }}</span>
              </div>
              <p
                class="return-instructions w-100 text-muted mt-3 text-center text-md-start"
                v-html="$t('purchase.returnInstractions')"
              />
            </div>
          </template>
        </ValidationField>
      </div>
    </ValidationGroup>
  </div>
</template>

<script>
import {
  mapState, mapActions, mapMutations, mapGetters,
} from 'vuex';
import BanksList from './BanksList';
import Checkbox from './Checkbox';
import ValidationField from './ValidationField';
import ValidationGroup from './ValidationGroup';
import ConfirmationForm from './ConfirmationForm';
import Messages from './Messages';

export default {
  props: {
    product: { type: Object, required: true },
    startPurchase: { type: Function, required: true },
    price: Number,
  },
  components: {
    Checkbox,
    BanksList,
    ValidationField,
    ValidationGroup,
    ConfirmationForm,
    Messages,
  },
  data: () => ({
    readContract: false,
    purchaseSession: null,
    form: {},
    details: {
      bankId: '',
      confirmationEmail: '',
    },
    banksLinks: [
      { code: 'swed' },
      { code: 'seb' },
      { code: 'lhv' },
      // { code: 'luminor' },
      { code: 'coop' },
    ],
  }),
  watch: {
    purchaseSession(session) {
      if (session && session.url) {
        window.location.href = session.url;
      }
    },
  },
  computed: {
    ...mapState('session', ['profile']),
    ...mapGetters('product', ['vatPercent']),

    calculatedPrice() {
      return this.price || this.product.price;
    },
  },
  methods: {
    ...mapActions('alerts', ['showMessage']),
    ...mapMutations('session', ['updateProfile']),

    pay({ valid }) {
      if (!valid) {
        return this.showMessage({
          target: 'purchase',
          type: 'danger',
          text: this.$t('purchase.validation.error'),
        });
      }

      if (this.details.confirmationEmail !== this.profile.email) {
        this.updateProfile({ confirmationEmail: this.details.confirmationEmail });
      }

      const params = {
        ...this.details,
        productCode: this.product.productCode,
        price: this.calculatedPrice,
        successUrl: this.$generateUrl({
          name: `purchaseHistory-${this.$i18n.locale}`,
          query: { product: this.product.productCode, lang: this.$i18n.locale },
        }),
        cancelUrl: this.$generateUrl({
          name: `purchase-${this.$i18n.locale}`,
          query: { fail: 1, lang: this.$i18n.locale },
        }),
      };

      return this.startPurchase(params)
        .then((session) => this.purchaseSession = session);
    },

    setUserEmail() {
      this.details.confirmationEmail = this.profile.confirmationEmail || this.profile.email;
    },
  },

  created() {
    this.setUserEmail();
  },

  mounted() {
    this.$watch(() => this.$refs.form.state, (state) => this.form = state);
  },
};
</script>
